import { LoadingOutlined } from '@ant-design/icons';
import { ThemedLayout } from '@refinedev/antd';
import { Authenticated, ErrorComponent } from '@refinedev/core';
import { ConfigProvider } from 'antd';
import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';

import CountryEnforcer from '@/components/general/CountryEnforcer';
import ErrorBoundary from '@/components/general/ErrorBoundary';
import SpinLoading from '@/components/general/SpinLoading';
import Header from '@/components/layout/Header/Header';
import CustomSider from '@/components/layout/Sider/Sider';
import ProtectedRoutes from '@/components/protected/ProtectedRoute';
import useConfigProvider from '@/hooks/useConfigProvider';
import { useFirebaseMessaging } from '@/hooks/useFirebaseMessaging';
import { RESOURCES } from '@/Resources';

import { LoginPage } from './auth/LoginPage';
import LoginSection from './auth/LoginSection';
import Home from './home/Home';

// Code-splitting por sección (item M5 de MEJORAS_PROPUESTAS.md): Home/Login quedan en el
// bundle inicial y las secciones autenticadas bajan bajo demanda.
const NotificationsSection = lazy(() => import('./notifications/NotificationsSection'));
const ShipmentSection = lazy(() => import('./logistic/Shipments/ShipmentSection'));
const ShipmentsList = lazy(() =>
  import('./logistic/Shipments/ShipmentsList').then(module => ({ default: module.ShipmentsList })),
);
const ShipmentShow = lazy(() =>
  import('./logistic/Shipments/ShipmentShow').then(module => ({ default: module.ShipmentShow })),
);
const ShipmentEdit = lazy(() =>
  import('./logistic/Shipments/ShipmentEdit').then(module => ({ default: module.ShipmentEdit })),
);
const ShipmentCreate = lazy(() =>
  import('./logistic/Shipments/ShipmentCreate').then(module => ({
    default: module.ShipmentCreate,
  })),
);
const ShippingLabelsSection = lazy(() => import('./logistic/ShippingLabels/ShippingLabelsSection'));
const ShippingLabelsList = lazy(() => import('./logistic/ShippingLabels/ShippingLabelsList'));
const ShippingLabelShow = lazy(() =>
  import('./logistic/ShippingLabels/ShippingLabelShow').then(module => ({
    default: module.ShippingLabelShow,
  })),
);
const ShippingLabelEdit = lazy(() => import('./logistic/ShippingLabels/ShippingLabelEdit'));
const ShippingLabelCreate = lazy(() =>
  import('./logistic/ShippingLabels/ShippingLabelCreate').then(module => ({
    default: module.ShippingLabelCreate,
  })),
);
const ShipperSection = lazy(() => import('./logistic/Shippers/ShipperSection'));
const ShippersList = lazy(() => import('./logistic/Shippers/ShippersList'));
const ShipperShow = lazy(() => import('./logistic/Shippers/ShipperShow'));
const ShipperEdit = lazy(() => import('./logistic/Shippers/ShipperEdit'));
const ShipperCreate = lazy(() => import('./logistic/Shippers/ShipperCreate'));
const ReceptionSection = lazy(() => import('./logistic/Receptions/ReceptionSection'));
const ReceptionsList = lazy(() =>
  import('./logistic/Receptions/ReceptionsList').then(module => ({
    default: module.ReceptionsList,
  })),
);
const ReceptionShow = lazy(() =>
  import('./logistic/Receptions/ReceptionShow').then(module => ({
    default: module.ReceptionShow,
  })),
);
const ReceptionEdit = lazy(() =>
  import('./logistic/Receptions/ReceptionEdit').then(module => ({
    default: module.ReceptionEdit,
  })),
);
const ReceptionCreate = lazy(() =>
  import('./logistic/Receptions/ReceptionCreate').then(module => ({
    default: module.ReceptionCreate,
  })),
);
const ServiceOrdersSection = lazy(() => import('./ServiceOrder/ServiceOrdersSection'));
const ServiceOrdersList = lazy(() => import('./ServiceOrder/ServiceOrdersList'));
const ServiceOrderCreate = lazy(() => import('./ServiceOrder/ServiceOrderCreate'));
const ServiceOrderShow = lazy(() => import('./ServiceOrder/ServiceOrderShow'));
const CaseResolutionSection = lazy(() => import('./logistic/CaseResolution/CaseResolutionSection'));
const CaseResolutionList = lazy(() => import('./logistic/CaseResolution/CaseResolutionList'));
const CaseResolutionShow = lazy(() => import('./logistic/CaseResolution/CaseResolutionShow'));
const SparePartsCollectionsSection = lazy(
  () => import('./administration/SparePartsCollection/SparePartsCollectionsSection'),
);
const SparePartsCollectionList = lazy(
  () => import('./administration/SparePartsCollection/SparePartsCollectionList'),
);
const ProductCatalogSection = lazy(() => import('./productCatalog/ProductCatalogSection'));
const ProductCatalogList = lazy(() => import('./productCatalog/ProductCatalogList'));
const TicketSection = lazy(() => import('./tickets/TicketSection'));
const TicketList = lazy(() => import('./tickets/TicketList'));
const TicketShow = lazy(() =>
  import('./tickets/TicketShow').then(module => ({ default: module.TicketShow })),
);
const TicketEdit = lazy(() =>
  import('./tickets/TicketEdit').then(module => ({ default: module.TicketEdit })),
);
const TicketCreate = lazy(() => import('./tickets/TicketCreate'));
/**
 * @metadata SafeLoginRedirect
 * Evita encadenar el query param `to` para no generar loops de redirección.
 */
const SafeLoginRedirect = ({ to }: { to: string }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  // Evita que un `to` previo se re-encodee y crezca en cada redirección.
  searchParams.delete('to');

  const sanitizedSearch = searchParams.toString();
  const sanitizedPath = `${location.pathname}${sanitizedSearch ? `?${sanitizedSearch}` : ''}${
    location.hash ?? ''
  }`;
  const nextSearch = new URLSearchParams();

  nextSearch.set('to', sanitizedPath || '/');

  // Deja que el router agregue el basename para evitar duplicarlo.
  return <Navigate to={{ pathname: to, search: `?${nextSearch.toString()}` }} replace />;
};

// Mantiene estable la referencia del Header para que ThemedLayout no remonte el menu al cambiar idioma.
const LayoutHeader = () => <Header />;
// Mantiene estable la referencia del Sider junto con el Header dentro de ThemedLayout.
const LayoutSider = () => <CustomSider />;
// Mantiene estable el icono de carga que recibe Authenticated en las rutas publicas.
const authenticatedPagesLoading = <LoadingOutlined />;
const AppRoutes = () => {
  useFirebaseMessaging();
  const { antdLocale, theme } = useConfigProvider();

  return (
    <Routes>
      <Route
        element={
          <ConfigProvider locale={antdLocale} theme={theme}>
            <Authenticated key="authenticated-inner" fallback={<SafeLoginRedirect to="/login" />}>
              <ErrorBoundary>
                <ThemedLayout Header={LayoutHeader} Sider={LayoutSider}>
                  <ProtectedRoutes>
                    <CountryEnforcer>
                      <Outlet />
                    </CountryEnforcer>
                  </ProtectedRoutes>
                </ThemedLayout>
              </ErrorBoundary>
            </Authenticated>
          </ConfigProvider>
        }>
        <Route path="">
          <Route index element={<Home />} />
        </Route>
        <Route
          path="notifications"
          element={
            <Suspense fallback={<SpinLoading />}>
              <NotificationsSection />
            </Suspense>
          }
        />
        <Route
          path={RESOURCES.TICKETS}
          element={
            <Suspense fallback={<SpinLoading />}>
              <TicketSection />
            </Suspense>
          }>
          <Route index element={<TicketList />} />
          <Route path="show/:id" element={<TicketShow />} />
          <Route path="edit/:id" element={<TicketEdit />} />
          <Route path="create" element={<TicketCreate />} />
        </Route>
        <Route
          path={RESOURCES.SHIPMENTS}
          element={
            <Suspense fallback={<SpinLoading />}>
              <ShipmentSection />
            </Suspense>
          }>
          <Route index element={<ShipmentsList />} />
          <Route path="show/:id" element={<ShipmentShow />} />
          <Route path="edit/:id" element={<ShipmentEdit />} />
          <Route path="create" element={<ShipmentCreate />} />
        </Route>
        <Route
          path={RESOURCES.SHIPPING_LABELS}
          element={
            <Suspense fallback={<SpinLoading />}>
              <ShippingLabelsSection />
            </Suspense>
          }>
          <Route index element={<ShippingLabelsList />} />
          <Route path="show/:id" element={<ShippingLabelShow />} />
          <Route path="edit/:id" element={<ShippingLabelEdit />} />
          <Route path="create" element={<ShippingLabelCreate />} />
        </Route>
        <Route
          path={RESOURCES.SHIPPERS}
          element={
            <Suspense fallback={<SpinLoading />}>
              <ShipperSection />
            </Suspense>
          }>
          <Route index element={<ShippersList />} />
          <Route path="show/:id" element={<ShipperShow />} />
          <Route path="edit/:id" element={<ShipperEdit />} />
          <Route path="create" element={<ShipperCreate />} />
        </Route>
        <Route
          path={RESOURCES.RECEPTIONS}
          element={
            <Suspense fallback={<SpinLoading />}>
              <ReceptionSection />
            </Suspense>
          }>
          <Route index element={<ReceptionsList />} />
          <Route path="show/:id" element={<ReceptionShow />} />
          <Route path="edit/:id" element={<ReceptionEdit />} />
          <Route path="create" element={<ReceptionCreate />} />
        </Route>
        <Route
          path={RESOURCES.SERVICE_ORDERS}
          element={
            <Suspense fallback={<SpinLoading />}>
              <ServiceOrdersSection />
            </Suspense>
          }>
          <Route index element={<ServiceOrdersList />} />
          <Route path="create" element={<ServiceOrderCreate />} />
          <Route path="show/:id" element={<ServiceOrderShow />} />
        </Route>
        <Route
          path={RESOURCES.CASE_RESOLUTION}
          element={
            <Suspense fallback={<SpinLoading />}>
              <CaseResolutionSection />
            </Suspense>
          }>
          <Route index element={<CaseResolutionList />} />
          <Route path=":id" element={<CaseResolutionShow />} />
        </Route>
        <Route
          path={RESOURCES.SPARE_PARTS_COLLECTIONS}
          element={
            <Suspense fallback={<SpinLoading />}>
              <SparePartsCollectionsSection />
            </Suspense>
          }>
          <Route index element={<SparePartsCollectionList />} />
          <Route path=":id" element={<ShipmentShow />} />
        </Route>
        <Route
          path={RESOURCES.PRODUCT_CATALOG}
          element={
            <Suspense fallback={<SpinLoading />}>
              <ProductCatalogSection />
            </Suspense>
          }>
          <Route index element={<ProductCatalogList />} />
        </Route>
        <Route path="*" element={<ErrorComponent />} />
      </Route>
      <Route
        element={
          <Authenticated
            key="authenticated-pages"
            fallback={<Outlet />}
            loading={authenticatedPagesLoading}>
            <Navigate to={'/'} />
          </Authenticated>
        }>
        <Route path="login" element={<LoginSection />}>
          <Route index element={<LoginPage />} />
        </Route>
      </Route>
    </Routes>
  );
};

export default AppRoutes;
